import {
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  Toaster as SonnerToaster,
  toast as sonnerToast,
  type ExternalToast,
  type ToastT,
  type ToastToDismiss,
  type ToasterProps,
} from 'sonner';

import type { MonkeysToastMessages } from '../../locale';
import { useMonkeysLocaleMessages } from '../../provider/context';

export type ToastVariant = 'error' | 'warning' | 'success' | 'info';

export type ToastInput = Omit<ExternalToast, 'description'> & {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
};

export type MonkeysToasterProps = ToasterProps & {
  useThemeClassNames?: boolean;
};

export type MonkeysToastProviderProps = {
  children: ReactNode;
  toasterProps?: MonkeysToasterProps;
};

type ToastValue =
  | Error
  | string
  | {
      message?: unknown;
      title?: unknown;
    }
  | null
  | undefined;

type ToastResolver<T> = T | ((value: ToastValue) => T);
type ToastTranslate = (value: string, params?: Record<string, unknown>) => string;

type ToastValueOptions = {
  variant?: ToastResolver<ToastVariant | undefined>;
  title?: ToastResolver<string | undefined>;
  description?: ToastResolver<string | undefined>;
  fallbackDescription?: ToastResolver<string | undefined>;
  signature?: ToastResolver<string | undefined>;
  duration?: ToastResolver<number | undefined>;
  translate?: ToastTranslate;
  messages?: Partial<ToastDefaultMessages>;
};

type ToastFeedEntry =
  | {
      variant?: ToastVariant;
      title?: string;
      description?: string;
      duration?: number;
      signature?: string;
    }
  | null
  | undefined;

type ToastDefaultMessages = MonkeysToastMessages;

type ToastPromiseInput<T = unknown> = Promise<T> | (() => Promise<T>);
type ToastPromiseReturn<T = unknown> = string | number | { unwrap: () => Promise<T> };

export type MonkeysToastApi = {
  (message: ReactNode, options?: ExternalToast): string | number;
  show: (input: ToastInput) => string | number | undefined;
  error: (input: ReactNode | ToastInput, options?: ExternalToast) => string | number | undefined;
  warning: (input: ReactNode | ToastInput, options?: ExternalToast) => string | number | undefined;
  success: (input: ReactNode | ToastInput, options?: ExternalToast) => string | number | undefined;
  info: (input: ReactNode | ToastInput, options?: ExternalToast) => string | number | undefined;
  message: (message: ReactNode, options?: ExternalToast) => string | number;
  promise: <T = unknown>(promise: ToastPromiseInput<T>, data?: unknown) => ToastPromiseReturn<T>;
  dismiss: (id?: number | string) => string | number;
  loading: (message: ReactNode, options?: ExternalToast) => string | number;
  custom: (jsx: (id: number | string) => ReactElement, data?: ExternalToast) => string | number;
  getHistory: () => Array<ToastT | ToastToDismiss>;
  getToasts: () => Array<ToastT | ToastToDismiss>;
};

const DEFAULT_TOAST_DURATION = 5200;
const TOAST_DEDUPE_WINDOW = 1200;

const ACTIONABLE_TOAST_PATTERNS = [
  /^(please|select|enter|provide|write|choose|confirm|fill)\b/i,
  /^(请|请选择|请先|请输入|请填写|请确认|请留意)/,
];

const DEFAULT_TOAST_CLASS_NAMES = {
  toast:
    'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
  description: 'group-[.toast]:text-muted-foreground',
  actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
  cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
};

const recentToastSignatures = new Map<string, number>();

function cn(...classNames: Array<string | undefined | false>) {
  return classNames.filter(Boolean).join(' ');
}

function isToastInput(input: unknown): input is ToastInput {
  if (!input || typeof input !== 'object' || isValidElement(input)) {
    return false;
  }

  return 'title' in input || 'description' in input || 'variant' in input;
}

function isEmptyToastContent(value: ReactNode) {
  return value == null || (typeof value === 'string' && !value.trim());
}

function normalizeToastInput(input: ReactNode | ToastInput, variant: ToastVariant, options?: ExternalToast): ToastInput {
  const { description: _description, ...baseOptions } = options ?? {};

  if (isToastInput(input)) {
    return {
      ...baseOptions,
      ...input,
      variant: input.variant ?? variant,
    };
  }

  return {
    ...baseOptions,
    title: input,
    variant,
  };
}

function resolveValue<T>(candidate: ToastResolver<T> | undefined, value: ToastValue) {
  if (typeof candidate === 'function') {
    return (candidate as (input: ToastValue) => T)(value);
  }

  return candidate;
}

function shouldEmitToast(signature: string) {
  const now = Date.now();

  for (const [key, timestamp] of recentToastSignatures.entries()) {
    if (now - timestamp > TOAST_DEDUPE_WINDOW) {
      recentToastSignatures.delete(key);
    }
  }

  const previousTimestamp = recentToastSignatures.get(signature);
  if (previousTimestamp && now - previousTimestamp < TOAST_DEDUPE_WINDOW) {
    return false;
  }

  recentToastSignatures.set(signature, now);
  return true;
}

function translateMessage(translate: ToastTranslate | undefined, value: string) {
  return translate ? translate(value, { defaultValue: value }) : value;
}

function getDefaultMessage(
  messages: Partial<ToastDefaultMessages> | undefined,
  localeMessages: ToastDefaultMessages,
  key: keyof ToastDefaultMessages,
) {
  return messages?.[key] ?? localeMessages[key];
}

function emitToast(input: ToastInput) {
  const { title, description, duration, variant = 'info', ...options } = input;
  const message = isEmptyToastContent(title) ? description : title;

  if (isEmptyToastContent(message)) {
    return undefined;
  }

  const descriptionValue =
    !isEmptyToastContent(description) && description !== message ? description : undefined;
  const sonnerOptions: ExternalToast = {
    ...options,
    description: descriptionValue,
    duration: duration ?? DEFAULT_TOAST_DURATION,
  };

  switch (variant) {
    case 'error':
      return sonnerToast.error(message, sonnerOptions);
    case 'warning':
      return sonnerToast.warning(message, sonnerOptions);
    case 'success':
      return sonnerToast.success(message, sonnerOptions);
    default:
      return sonnerToast(message, sonnerOptions);
  }
}

export function extractToastMessage(value: ToastValue, fallback = '') {
  if (!value) return fallback;

  if (typeof value === 'string') {
    const nextMessage = value.trim();
    return nextMessage || fallback;
  }

  if (value instanceof Error) {
    const nextMessage = String(value.message || '').trim();
    return nextMessage || fallback;
  }

  if (typeof value === 'object' && typeof value.message === 'string') {
    const nextMessage = value.message.trim();
    if (nextMessage) return nextMessage;
  }

  if (typeof value === 'object' && typeof value.title === 'string') {
    const nextTitle = value.title.trim();
    if (nextTitle) return nextTitle;
  }

  return fallback;
}

export function resolveToastVariantForMessage(value: ToastValue): ToastVariant {
  const message = extractToastMessage(value);

  if (ACTIONABLE_TOAST_PATTERNS.some((pattern) => pattern.test(message))) {
    return 'warning';
  }

  return 'error';
}

export function MonkeysToaster({
  className,
  closeButton = true,
  position = 'bottom-right',
  richColors = true,
  toastOptions,
  useThemeClassNames = true,
  visibleToasts = 10,
  ...props
}: MonkeysToasterProps) {
  return (
    <SonnerToaster
      className={cn('pointer-events-auto toaster group', className)}
      closeButton={closeButton}
      position={position}
      richColors={richColors}
      toastOptions={{
        ...toastOptions,
        classNames: useThemeClassNames
          ? {
              ...DEFAULT_TOAST_CLASS_NAMES,
              ...toastOptions?.classNames,
            }
          : toastOptions?.classNames,
      }}
      visibleToasts={visibleToasts}
      {...props}
    />
  );
}

export function MonkeysToastProvider({ children, toasterProps }: MonkeysToastProviderProps) {
  return (
    <>
      {children}
      <MonkeysToaster {...toasterProps} />
    </>
  );
}

export function useToastOnValue(value: ToastValue, options: ToastValueOptions = {}) {
  const previousSignatureRef = useRef<string | null>(null);
  const localeMessages = useMonkeysLocaleMessages().toast;
  const translate = options.translate;

  useEffect(() => {
    if (!value) {
      previousSignatureRef.current = null;
      return;
    }

    const variant = resolveValue(options.variant, value) ?? 'error';
    const rawTitle = resolveValue(options.title, value);
    const rawFallbackDescription = resolveValue(options.fallbackDescription, value);
    const rawDescription =
      resolveValue(options.description, value) ?? extractToastMessage(value, rawFallbackDescription || '');
    const title = translateMessage(
      translate,
      rawTitle ||
        getDefaultMessage(
          options.messages,
          localeMessages,
          variant === 'warning' ? 'warningTitle' : 'errorTitle',
        ),
    );
    const description = translateMessage(
      translate,
      rawDescription ||
        getDefaultMessage(
          options.messages,
          localeMessages,
          variant === 'warning' ? 'defaultWarningDescription' : 'defaultErrorDescription',
        ),
    );
    const signature = resolveValue(options.signature, value) || [variant, title, description].join('|');

    if (previousSignatureRef.current === signature) {
      return;
    }

    previousSignatureRef.current = signature;

    if (!shouldEmitToast(signature)) {
      return;
    }

    toast.show({
      variant,
      title,
      description,
      duration: resolveValue(options.duration, value),
    });
  }, [
    options.description,
    options.duration,
    options.fallbackDescription,
    options.messages,
    options.signature,
    options.title,
    options.variant,
    localeMessages,
    translate,
    value,
  ]);
}

export function useToastFeed(items: ToastFeedEntry[], options: Pick<ToastValueOptions, 'messages' | 'translate'> = {}) {
  const previousVisibleSignaturesRef = useRef<Set<string>>(new Set());
  const localeMessages = useMonkeysLocaleMessages().toast;
  const translate = options.translate;
  const emitItem = useCallback(
    (item: NonNullable<ToastFeedEntry>, index: number, visibleSignatures: Set<string>) => {
      const variant = item.variant ?? 'info';
      const title = translateMessage(
        translate,
        item.title ||
          getDefaultMessage(
            options.messages,
            localeMessages,
            variant === 'warning' ? 'warningTitle' : 'errorTitle',
          ),
      );
      const description = translateMessage(
        translate,
        item.description ||
          getDefaultMessage(
            options.messages,
            localeMessages,
            variant === 'warning' ? 'defaultWarningDescription' : 'defaultErrorDescription',
          ),
      );
      const signature = item.signature || [variant, title, description, index].join('|');

      visibleSignatures.add(signature);

      if (previousVisibleSignaturesRef.current.has(signature) || !shouldEmitToast(signature)) {
        return;
      }

      toast.show({
        variant,
        title,
        description,
        duration: item.duration,
      });
    },
    [localeMessages, options.messages, translate],
  );

  useEffect(() => {
    const visibleSignatures = new Set<string>();

    items.forEach((item, index) => {
      if (item) {
        emitItem(item, index, visibleSignatures);
      }
    });

    previousVisibleSignaturesRef.current = visibleSignatures;
  }, [emitItem, items]);
}

export const toast: MonkeysToastApi = Object.assign(
  (message: ReactNode, options?: ExternalToast) => sonnerToast(message, options),
  {
    show: emitToast,
    error: (input: ReactNode | ToastInput, options?: ExternalToast) =>
      emitToast(normalizeToastInput(input, 'error', options)),
    warning: (input: ReactNode | ToastInput, options?: ExternalToast) =>
      emitToast(normalizeToastInput(input, 'warning', options)),
    success: (input: ReactNode | ToastInput, options?: ExternalToast) =>
      emitToast(normalizeToastInput(input, 'success', options)),
    info: (input: ReactNode | ToastInput, options?: ExternalToast) =>
      emitToast(normalizeToastInput(input, 'info', options)),
    message: sonnerToast.message,
    promise: sonnerToast.promise as MonkeysToastApi['promise'],
    dismiss: sonnerToast.dismiss,
    loading: sonnerToast.loading,
    custom: sonnerToast.custom as (jsx: (id: number | string) => ReactElement, data?: ExternalToast) => string | number,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  },
);

export { MonkeysToaster as Toaster, MonkeysToastProvider as ToastProvider };
export type { ExternalToast, ToasterProps };
